import 'whatwg-fetch'
import s$ from 'signwell'

const urlDefaults = {
  scheme: DOCSKETCH_APP_SCHEME,
  host: `${DOCSKETCH_APP_HOST.replace('https://', '')}`,
  pathPrefix: `${RELATIVE_URL_ROOT}api/`,
  path: ''
}

const defaultOptions = {
  credentials: 'same-origin',
  redirect: 'follow'
}

const contentHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json'
}

export function get (path, query) {
  return request(path, { query })
}

export function post (path, data) {
  return request(path, {
    method: 'POST',
    body: JSON.stringify(data)
  })
}

export function formPost (path, form) {
  return request(path, {
    method: 'POST',
    body: form,
    includeContentHeaders: false
  })
}

export function put (path, data) {
  return request(path, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

export function del (path, data = {}) {
  return request(path, {
    method: 'DELETE',
    body: JSON.stringify(data)
  })
}

function request (path, options = {}) {
  const { query } = options
  const url = buildUrl({ path, query })
  const { includeContentHeaders = true, ...restOptions } = options
  let fetchOptions = { ...defaultOptions, ...restOptions }

  const mandatoryHeaders = {
    Authorization: `Bearer token=${s$.accessToken}`
  }

  fetchOptions = {
    ...fetchOptions,
    headers: includeContentHeaders
      ? { ...contentHeaders, ...mandatoryHeaders }
      : mandatoryHeaders
  }

  return fetch(url, fetchOptions)
    .then(handleResponse)
    .catch(error => {
      const productionOrTest = ['production', 'test'].includes(process.env.NODE_ENV)
      if (!productionOrTest) {
        console.group(error.message)
        console.warn(error)
        console.info(error.details)
        console.info(fetchOptions)
        console.groupEnd()
      }
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        error.humanReadable = 'We have problems processing the request, please try again later.'
      }

      throw error
    })
}

export function xhrPut (path, formData, handleProgress) {
  return xhrRequest(path, {
    formData,
    action: 'PUT',
    handleProgress
  })
}

export function xhrRequest (path, options = {}) {
  return new Promise((resolve, reject) => {
    const {
      query,
      formData,
      action = 'POST',
      handleProgress = () => {}
    } = options

    const url = buildUrl({ path, query })
    const xhr = new global.XMLHttpRequest()
    xhr.upload.addEventListener('progress', handleProgress)
    xhr.open(action, url)
    xhr.setRequestHeader('Authorization', 'Bearer token=' + s$.accessToken)
    xhr.onload = () => {
      switch (true) {
        case xhr.status === 204:
          resolve(xhr)
          break
        case xhr.status < 400:
          resolve(JSON.parse(xhr.response))
          break
        case xhr.status === 413:
          reject(Error('Request too large'))
          break
        case xhr.status >= 400:
          reject(RequestError(xhr.response, JSON.parse(xhr.response)))
          break
        default:
          reject(Error('Something else went wrong'))
          break
      }
    }
    xhr.onerror = () => reject(Error('There was some type of error'))
    xhr.send(formData)
  })
}

export function rawXhrRequest (url, options = {}) {
  return new Promise((resolve, reject) => {
    const { formData, action = 'POST', handleProgress = () => {} } = options

    const xhr = new global.XMLHttpRequest()
    xhr.upload.addEventListener('progress', handleProgress)
    xhr.open(action, url)
    xhr.setRequestHeader('Access-Control-Allow-Origin', '*')
    xhr.onload = () => {
      switch (true) {
        case xhr.status === 204:
          resolve(xhr)
          break
        case xhr.status < 400:
          resolve(xhr)
          break
        case xhr.status === 413:
          reject(Error('Request too large'))
          break
        case xhr.status >= 400:
          reject(RequestError(xhr.response, JSON.parse(xhr.response)))
          break
        default:
          reject(Error('Something else went wrong'))
          break
      }
    }
    xhr.onerror = () => reject(Error('There was some type of error'))
    xhr.send(formData)
  })
}

function buildUrl (urlOptions) {
  const { scheme, host, pathPrefix, path, query = {} } = {
    ...urlDefaults,
    ...urlOptions
  }
  return `${scheme}://${host}${pathPrefix}${path}/${buildQueryString(query)}`
}

function buildQueryString (query = {}) {
  if (Object.keys(query).length === 0) {
    return ''
  }
  return (
    '?' +
    Object.entries(query)
      .map(([key, value]) => {
        return encodeURIComponent(key) + '=' + encodeURIComponent(value)
      })
      .join('&')
  )
}

function handleResponse (response) {
  if (response.ok) {
    switch (response.status) {
      case 204: // No Content
        return response
      default:
        // All other 2xx statuses
        return response.json()
    }
  } else {
    // All Errors
    return response.json().then((body) => {
      throw new RequestError(response, body)
    })
  }
}

function messageReducer (previous, current) {
  const separator = previous[previous.length - 1] === '.' ? ' ' : '. '
  return `${previous}${separator}${current}`
}

class RequestError extends Error {
  constructor (response, body, ...rest) {
    super(rest)
    this.body = body
    this.details = body && body.errors && body.errors.reduce(messageReducer)
    this.humanReadable = body && body.messages && body.messages.reduce(messageReducer)
    this.message = response.statusText
    this.status = response.status
  }
}
